import React from 'react';

interface LevelGroundDamierProps {
  levelId: number;
  baseColor: string;
  isRestored?: boolean;
  isNight?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

export const LevelGroundDamier: React.FC<LevelGroundDamierProps> = React.memo(({
  levelId,
  baseColor,
  isRestored = false,
  isNight = false,
  className = "",
  style = {}
}) => {
  if (levelId === 4) {
    return (
      <svg
        className={`absolute inset-0 w-full h-full pointer-events-none select-none ${className}`}
        xmlns="http://www.w3.org/2000/svg"
        style={{ imageRendering: 'pixelated', ...style }}
      >
        <defs>
          {/* 1. Mode Base / Sanctuaire : Sol Gris pierre des Ancêtres (jamais rouge) */}
          <pattern
            id="damierPattern_4_corrupted"
            width="40"
            height="40"
            patternUnits="userSpaceOnUse"
          >
            <rect width="40" height="40" fill="#3f3f46" />
            <rect x="0" y="0" width="20" height="20" fill="#27272a" opacity="0.4" />
            <rect x="20" y="20" width="20" height="20" fill="#27272a" opacity="0.4" />
            <rect x="5" y="5" width="4" height="4" fill="#18181b" opacity="0.25" />
          </pattern>

          {/* 2. Mode Jour Purifié (Vert émeraude lumineux) */}
          <pattern
            id="damierPattern_4_day"
            width="40"
            height="40"
            patternUnits="userSpaceOnUse"
          >
            <rect width="40" height="40" fill="#16a34a" />
            <rect x="0" y="0" width="20" height="20" fill="#000000" opacity="0.05" />
            <rect x="20" y="20" width="20" height="20" fill="#000000" opacity="0.05" />
            <rect x="5" y="5" width="4" height="4" fill="#000000" opacity="0.1" />
          </pattern>

          {/* 3. Mode Nuit Épilogue (Dallage en pierre grise minérale sous le clair de lune avec lueurs éthérées) */}
          <pattern
            id="damierPattern_4_night"
            width="40"
            height="40"
            patternUnits="userSpaceOnUse"
          >
            <rect width="40" height="40" fill="#27272a" />
            <rect x="0" y="0" width="20" height="20" fill="#18181b" opacity="0.5" />
            <rect x="20" y="20" width="20" height="20" fill="#18181b" opacity="0.5" />
            <rect x="5" y="5" width="4" height="4" fill="#3f3f46" opacity="0.3" />
            <circle cx="12" cy="12" r="1" fill="#94a3b8" opacity="0.25" />
            <circle cx="32" cy="32" r="1.2" fill="#cbd5e1" opacity="0.2" />
          </pattern>
        </defs>

        {/* Couche Corrompue */}
        <rect
          width="100%"
          height="100%"
          fill="url(#damierPattern_4_corrupted)"
          style={{
            opacity: (isRestored || isNight) ? 0 : 1,
            transition: 'opacity 2500ms ease-in-out',
            pointerEvents: 'none'
          }}
        />

        {/* Couche Jour Purifié */}
        <rect
          width="100%"
          height="100%"
          fill="url(#damierPattern_4_day)"
          style={{
            opacity: (isRestored && !isNight) ? 1 : 0,
            transition: 'opacity 2500ms ease-in-out',
            pointerEvents: 'none'
          }}
        />

        {/* Couche Nuit Épilogue */}
        <rect
          width="100%"
          height="100%"
          fill="url(#damierPattern_4_night)"
          style={{
            opacity: isNight ? 1 : 0,
            transition: 'opacity 2500ms ease-in-out',
            pointerEvents: 'none'
          }}
        />
      </svg>
    );
  }

  const effectiveColor = isNight ? '#27272a' : baseColor;
  const patternId = `damierPattern_${levelId}_${isRestored ? 'restored' : 'normal'}_${isNight ? 'night' : 'day'}_${effectiveColor.replace(/[^a-zA-Z0-9]/g, '')}`;

  return (
    <svg
      className={`absolute inset-0 w-full h-full pointer-events-none select-none ${className}`}
      xmlns="http://www.w3.org/2000/svg"
      style={{ imageRendering: 'pixelated', ...style }}
    >
      <defs>
        {levelId === 2 ? (
          <pattern
            id={patternId}
            width="40"
            height="40"
            patternUnits="userSpaceOnUse"
          >
            {/* Manteau neigeux nocturne éclairé par la lune sur le Djurdjura */}
            <rect width="40" height="40" fill="#475569" />
            <rect x="0" y="0" width="20" height="20" fill="#4f5e75" />
            <rect x="20" y="20" width="20" height="20" fill="#4f5e75" />
            {/* Névés et ondulations de neige bleutée sous la nuit */}
            <rect x="2" y="14" width="16" height="5" rx="2" fill="#384556" opacity="0.4" />
            <rect x="22" y="32" width="14" height="4" rx="2" fill="#384556" opacity="0.4" />
            <rect x="4" y="2" width="12" height="4" rx="2" fill="#64748b" opacity="0.45" />
            <rect x="24" y="18" width="12" height="4" rx="2" fill="#64748b" opacity="0.45" />
            {/* Cristaux de givre et étincelles de neige au clair de lune */}
            <circle cx="8" cy="8" r="0.9" fill="#ffffff" opacity="0.85" />
            <circle cx="28" cy="6" r="1.1" fill="#f0f9ff" opacity="0.75" />
            <circle cx="15" cy="28" r="0.8" fill="#e0f2fe" opacity="0.8" />
            <circle cx="34" cy="26" r="1.2" fill="#ffffff" opacity="0.9" />
            <circle cx="20" cy="38" r="0.7" fill="#ffffff" opacity="0.7" />
          </pattern>
        ) : (
          <pattern
            id={patternId}
            width="40"
            height="40"
            patternUnits="userSpaceOnUse"
          >
            <rect width="40" height="40" fill={effectiveColor} />
            <rect x="0" y="0" width="20" height="20" fill="#000000" opacity={isNight ? 0.2 : 0.05} />
            <rect x="20" y="20" width="20" height="20" fill="#000000" opacity={isNight ? 0.2 : 0.05} />
            <rect x="5" y="5" width="4" height="4" fill="#000000" opacity={isNight ? 0.25 : 0.1} />
            {isNight && (
              <>
                <circle cx="12" cy="12" r="1" fill="#34d399" opacity="0.22" />
                <circle cx="32" cy="32" r="1.2" fill="#6ee7b7" opacity="0.18" />
              </>
            )}
          </pattern>
        )}
      </defs>
      <rect width="100%" height="100%" fill={`url(#${patternId})`} />
    </svg>
  );
});
